/**
 * Safe Typography — bypasses Ant Design EllipsisMeasure loops.
 * Supports standard heading props (id, aria-*) for a11y section titles.
 */

'use client'

import React from 'react'

type TitleLevel = 1 | 2 | 3 | 4 | 5

/** Maps to CSS type scale in shared/theme/styles/tokens.css */
const TITLE_SIZE: Record<TitleLevel, string> = {
  1: 'var(--font-size-display, 2.25rem)',
  2: 'var(--font-size-title-lg, 1.875rem)',
  3: 'var(--font-size-title-md, 1.5rem)',
  4: 'var(--font-size-title-sm, 1.25rem)',
  5: 'var(--font-size-body, 1rem)',
}

export const Title: React.FC<
  React.HTMLAttributes<HTMLHeadingElement> & {
    level?: TitleLevel
    children: React.ReactNode
  }
> = ({ level = 1, children, className, style, ...props }) => {
  const titleStyles: React.CSSProperties = {
    fontSize: TITLE_SIZE[level],
    fontWeight: 700,
    margin: 0,
    letterSpacing: 'var(--tracking-snug, -0.02em)',
    lineHeight: 'var(--leading-tight, 1.25)',
    ...style,
  }

  const shared = { className, style: titleStyles, ...props }

  switch (level) {
    case 2:
      return <h2 {...shared}>{children}</h2>
    case 3:
      return <h3 {...shared}>{children}</h3>
    case 4:
      return <h4 {...shared}>{children}</h4>
    case 5:
      return <h5 {...shared}>{children}</h5>
    case 1:
    default:
      return <h1 {...shared}>{children}</h1>
  }
}

export const Paragraph: React.FC<
  React.HTMLAttributes<HTMLParagraphElement> & {
    children: React.ReactNode
  }
> = ({ children, className, style, ...props }) => (
  <p
    className={className}
    style={{
      fontSize: 'var(--font-size-body, 1rem)',
      lineHeight: 'var(--leading-normal, 1.5)',
      margin: '0 0 1rem 0',
      ...style,
    }}
    {...props}
  >
    {children}
  </p>
)

export const Text: React.FC<
  React.HTMLAttributes<HTMLSpanElement> & {
    children: React.ReactNode
    type?: 'secondary' | 'success' | 'warning' | 'danger'
    strong?: boolean
  }
> = ({ children, className, style, type, strong, ...props }) => {
  const color =
    type === 'secondary'
      ? 'var(--ant-color-text-secondary, #666)'
      : type === 'success'
        ? 'var(--ant-color-success, #52c41a)'
        : type === 'warning'
          ? 'var(--ant-color-warning, #faad14)'
          : type === 'danger'
            ? 'var(--ant-color-error, #ff4d4f)'
            : 'inherit'

  return (
    <span
      className={className}
      style={{
        fontSize: 'var(--font-size-body, 1rem)',
        lineHeight: 'var(--leading-normal, 1.5)',
        fontWeight: strong ? 600 : 400,
        color,
        ...style,
      }}
      {...props}
    >
      {children}
    </span>
  )
}

export const Link: React.FC<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    children: React.ReactNode
  }
> = ({ children, className, style, href, ...props }) => {
  const linkStyles: React.CSSProperties = {
    fontSize: 'var(--font-size-body, 1rem)',
    lineHeight: 'var(--leading-normal, 1.5)',
    color: 'var(--ant-color-primary, #c4a052)',
    textDecoration: 'none',
    cursor: 'pointer',
    ...style,
  }

  if (href) {
    return (
      <a href={href} className={className} style={linkStyles} {...props}>
        {children}
      </a>
    )
  }

  return (
    <span className={className} style={linkStyles} {...props}>
      {children}
    </span>
  )
}

export const Typography = {
  Title,
  Paragraph,
  Text,
  Link,
}

export const SafeTypography = Typography
